// v66.2 doctrine: ONLY reports pushed by Marcus's RB pipeline are
// rendered to customers. The admin-side "upload a PDF to a property"
// surface, which previously wrote to `farms/{farmId}/uploadedReports/*`
// and to GCS at `farmReports/{farmId}/<reportId>.pdf`, has been removed
// in line with that doctrine. This page now exposes only:
//   1. Reports-hub copy editor (page title, subtitle, intro text — still
//      surfaced on the customer-facing reports page).
//   2. EditionUnlockManager — the lock/unlock manager for Marcus-pushed
//      editions; this is the canonical admin surface for every
//      Marcus-published year × package × property edition.
// firestore.rules + storage.rules have also been tightened to deny new
// writes to `uploadedReports/*` and `farmReports/*` so the legacy data
// remains as inert read-only back-compat material that PropertiesAdmin
// still cascade-deletes when a property is removed.

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import {
  AlertCircle,
  FileText,
  Lock,
  RotateCcw,
  Unlock,
  X,
} from 'lucide-react';
import { db } from '../../firebase';
import { EcointelLottie, EcointelLottieMark } from '../../components/EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import { mergeReportsHubCopy, buildReportsConfigForSave } from '../../constants/reportsHubCopy';
import {
  KNOWN_PACKAGE_CODES,
  packageDisplayFor,
  editionProbeIdsForFarm,
  parseReportPage,
} from '../../constants/reportPackages';
import { setReportYearUnlock } from '../../api';
import AdminTable from '../../components/admin/AdminTable';
import { useAdminData } from '../../components/admin/AdminLayout';

// ---------------------------------------------------------------------------
// v0.3 Phase 4 — edition unlock manager (admin-side)
// ---------------------------------------------------------------------------
// Lists every (property, package, year) report edition that has ever been
// published (current_version >= 1) and lets admin set/clear the unlock_until
// override.
//
// Probe list lives in editionProbeIdsForFarm: known codes, plus any package
// ids named in that property's reportPage. A catalogue label is stored on
// the edition row so the lock manager shows the publisher's name.
function editionPackageLabel(row, packageLabels) {
  return row.label || packageLabels[row.packageId] || packageDisplayFor(row.packageId).label;
}

function isUnlockActive(unlockUntil) {
  if (typeof unlockUntil !== 'string' || !unlockUntil) return false;
  const t = Date.parse(unlockUntil);
  return Number.isFinite(t) && t > Date.now();
}

function formatUnlockUntil(iso) {
  if (typeof iso !== 'string' || !iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function EditionUnlockManager({ farms, farmLabelById, formatError }) {
  // Seed package labels from the shared map up front so codes that
  // aren't in the `reportPackages/` Firestore catalogue (e.g. the
  // 7-code suite — `lhr`, `lhr+`, `lhb`, `sab`, `fop`, `vpa`, `esr`,
  // none of which are catalogue-seeded per v66.2 doctrine) still
  // render with their canonical human-readable label instead of as
  // raw shortcodes. The catalogue load below overlays its labels on
  // top, so any catalogue-defined override (e.g. archived label
  // suffix on the legacy 4) wins. This is what fixes the admin
  // console blindness Marcus's 2026-05-19 7-code-suite push exposed.
  const [packageLabels, setPackageLabels] = useState(() => {
    const m = {};
    KNOWN_PACKAGE_CODES.forEach((id) => { m[id] = packageDisplayFor(id).label; });
    return m;
  });
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showAll, setShowAll] = useState(false);

  // Filter chip state
  const [packageFilter, setPackageFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);

  const [unlockModal, setUnlockModal] = useState(null);
  const [unlockUntilInput, setUnlockUntilInput] = useState('');
  const [unlockReason, setUnlockReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [clearingKey, setClearingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'reportPackages'));
        if (cancelled) return;
        // Overlay catalogue labels on top of the shared-map seed —
        // the seed already covers every KNOWN_PACKAGE_CODE, so any
        // catalogue doc just refines (e.g. the legacy 4 carry the
        // "(legacy)" suffix in the catalogue, while the shared map
        // carries the same suffix for back-compat). Either way, no
        // code goes label-less.
        setPackageLabels((prev) => {
          const next = { ...prev };
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            if (data.label) next[d.id] = data.label;
          });
          return next;
        });
      } catch (e) {
        console.warn('[EditionUnlockManager] reportPackages load failed (using shared PACKAGE_DISPLAY map):', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!Array.isArray(farms) || farms.length === 0) {
      setEditions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const all = [];
        const tasks = [];
        for (const farm of farms) {
          const page = parseReportPage(farm.reportPage);
          const labelById = new Map(
            (page ? page.packages : []).map((p) => [p.package_id, p.label]),
          );
          for (const packageId of editionProbeIdsForFarm(farm)) {
            tasks.push((async () => {
              try {
                const snap = await getDocs(collection(db, 'farms', farm.id, 'reports', packageId, 'years'));
                snap.docs.forEach((d) => {
                  const data = d.data() || {};
                  if (typeof data.current_version !== 'number' || data.current_version < 1) return;
                  all.push({
                    propertyId: farm.id,
                    packageId,
                    label: labelById.get(packageId) || null,
                    year: Number(d.id) || d.id,
                    current_version: data.current_version,
                    updatedAt: data.updatedAt || null,
                    locked: data.locked === true,
                    unlock_until: typeof data.unlock_until === 'string' ? data.unlock_until : null,
                    last_unlock_action: data.last_unlock_action || null,
                  });
                });
              } catch (e) {
                console.warn(`[EditionUnlockManager] read failed for ${farm.id}/${packageId}/years:`, e.message);
              }
            })());
          }
        }
        await Promise.all(tasks);
        if (cancelled) return;
        // Default order: property (alpha) → package (canonical display
        // order so the 7-code suite surfaces above legacy 4 + SKUs)
        // → year (newest first). User can resort by clicking column
        // headers — sortValue handlers below take it from there.
        all.sort((a, b) => {
          if (a.propertyId !== b.propertyId) return a.propertyId.localeCompare(b.propertyId);
          if (a.packageId !== b.packageId) {
            return packageDisplayFor(a.packageId).order - packageDisplayFor(b.packageId).order;
          }
          return Number(b.year) - Number(a.year);
        });
        setEditions(all);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [farms, refreshTick]);

  const yearOptions = useMemo(() => {
    const ys = new Set(editions.map((r) => String(r.year)));
    return Array.from(ys).sort((a, b) => Number(b) - Number(a));
  }, [editions]);

  const propertyOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    editions.forEach((r) => {
      if (seen.has(r.propertyId)) return;
      seen.add(r.propertyId);
      out.push({ id: r.propertyId, label: farmLabelById[r.propertyId] || r.propertyId });
    });
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [editions, farmLabelById]);

  // Data-driven package filter: only show chips for codes that have at
  // least one published edition somewhere on `editions`. With the probe
  // list now spanning the full KNOWN_PACKAGE_CODES (15 codes) showing
  // every code as a chip would overwhelm the toolbar — and most legacy
  // codes will have zero hits on most properties anyway. Sorted by
  // `packageDisplayFor(id).order` so the 7-code suite surfaces first.
  const packageOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    editions.forEach((r) => {
      if (seen.has(r.packageId)) return;
      seen.add(r.packageId);
      out.push({
        id: r.packageId,
        label: r.label || packageLabels[r.packageId] || packageDisplayFor(r.packageId).label,
      });
    });
    out.sort((a, b) => packageDisplayFor(a.id).order - packageDisplayFor(b.id).order);
    return out;
  }, [editions, packageLabels]);

  const baseRows = useMemo(() => {
    let rows = editions;
    if (!showAll) rows = rows.filter((r) => r.locked || r.unlock_until);
    if (packageFilter.length) rows = rows.filter((r) => packageFilter.includes(r.packageId));
    if (yearFilter.length) rows = rows.filter((r) => yearFilter.includes(String(r.year)));
    if (propertyFilter.length) rows = rows.filter((r) => propertyFilter.includes(r.propertyId));
    if (stateFilter.length) {
      rows = rows.filter((r) => {
        const active = isUnlockActive(r.unlock_until);
        const status = r.locked ? (active ? 'locked-override' : 'locked') : 'open';
        return stateFilter.includes(status);
      });
    }
    return rows;
  }, [editions, showAll, packageFilter, yearFilter, propertyFilter, stateFilter]);

  const openUnlockModal = (row) => {
    setUnlockModal(row);
    setUnlockUntilInput(isoToLocalInput(row.unlock_until) || isoToLocalInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()));
    setUnlockReason('');
    setActionError(null);
  };

  const closeUnlockModal = () => {
    if (submitting) return;
    setUnlockModal(null);
    setUnlockUntilInput('');
    setUnlockReason('');
    setActionError(null);
  };

  const handleSetUnlock = async () => {
    if (!unlockModal) return;
    const iso = localInputToIso(unlockUntilInput);
    if (!iso) {
      setActionError('Please pick a valid date and time.');
      return;
    }
    if (Date.parse(iso) <= Date.now()) {
      setActionError('Pick a time in the future. To remove an existing unlock, use Clear unlock.');
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      await setReportYearUnlock(unlockModal.propertyId, unlockModal.packageId, unlockModal.year, iso, unlockReason || undefined);
      setRefreshTick((n) => n + 1);
      closeUnlockModal();
    } catch (e) {
      setActionError(formatError ? formatError(e) : (e.message || 'Failed to set unlock.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearUnlock = async (row) => {
    const key = `${row.propertyId}|${row.packageId}|${row.year}`;
    const farmLabel = farmLabelById[row.propertyId] || row.propertyId;
    if (!window.confirm(`Clear unlock for ${farmLabel} → ${editionPackageLabel(row, packageLabels)} → ${row.year}?`)) return;
    setClearingKey(key);
    try {
      await setReportYearUnlock(row.propertyId, row.packageId, row.year, null, 'Cleared via admin UI');
      setRefreshTick((n) => n + 1);
    } catch (e) {
      alert(`Clear failed: ${formatError ? formatError(e) : e.message}`);
    } finally {
      setClearingKey(null);
    }
  };

  const filterGroups = [
    {
      id: 'state',
      label: 'State',
      selected: stateFilter,
      onChange: setStateFilter,
      options: [
        { value: 'open', label: 'Open' },
        { value: 'locked', label: 'Locked' },
        { value: 'locked-override', label: 'Locked (override active)' },
      ],
    },
    ...(packageOptions.length > 0 ? [{
      id: 'package',
      label: 'Package',
      selected: packageFilter,
      onChange: setPackageFilter,
      options: packageOptions.map((p) => ({ value: p.id, label: p.label })),
    }] : []),
    ...(yearOptions.length > 0 ? [{
      id: 'year',
      label: 'Year',
      selected: yearFilter,
      onChange: setYearFilter,
      options: yearOptions.map((y) => ({ value: y, label: y })),
    }] : []),
    ...(propertyOptions.length > 0 ? [{
      id: 'property',
      label: 'Property',
      selected: propertyFilter,
      onChange: setPropertyFilter,
      options: propertyOptions.map((p) => ({ value: p.id, label: p.label })),
    }] : []),
  ];

  const columns = [
    { key: 'property', label: 'Property', sortValue: (r) => (farmLabelById[r.propertyId] || r.propertyId).toLowerCase() },
    { key: 'package', label: 'Package', sortValue: (r) => editionPackageLabel(r, packageLabels).toLowerCase() },
    { key: 'year', label: 'Year', sortValue: (r) => Number(r.year) },
    { key: 'lock', label: 'Lock state', sortable: false },
    { key: 'unlock', label: 'Unlock until', sortValue: (r) => (r.unlock_until ? Date.parse(r.unlock_until) || 0 : 0) },
    { key: 'last', label: 'Last action', sortable: false },
    { key: 'actions', label: 'Actions', align: 'right', sortable: false, className: 'text-right' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <span className="font-bold text-sm text-slate-700 flex items-center gap-2">
              <Lock size={16} className="text-slate-500" />
              Edition lock manager
            </span>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Override the auto-lock on a published report edition so RB can push a corrected version.
              The unlock takes effect immediately and expires automatically at the chosen time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              Show all editions
            </label>
            <button
              type="button"
              onClick={() => setRefreshTick((n) => n + 1)}
              disabled={loading}
              className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 flex items-center gap-1 disabled:opacity-40"
              title="Refresh"
            >
              <RotateCcw size={12} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-6 text-center text-sm text-rose-600 flex items-center justify-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      ) : (
        <AdminTable
          rows={baseRows}
          columns={columns}
          rowKey={(r) => `${r.propertyId}|${r.packageId}|${r.year}`}
          loading={loading}
          searchPlaceholder="Filter by property, package, or year…"
          searchPredicate={(r, q) => {
            const farmLabel = (farmLabelById[r.propertyId] || r.propertyId).toLowerCase();
            const packageLabel = editionPackageLabel(r, packageLabels).toLowerCase();
            return (
              r.propertyId.toLowerCase().includes(q) ||
              farmLabel.includes(q) ||
              r.packageId.toLowerCase().includes(q) ||
              packageLabel.includes(q) ||
              String(r.year).includes(q)
            );
          }}
          filterGroups={filterGroups}
          defaultPageSize={25}
          emptyMessage={editions.length === 0
            ? 'No published report editions yet. Marcus’s pipeline has not pushed any reports for these properties.'
            : 'No editions currently need attention. Toggle "Show all editions" to see published-and-locked entries.'}
          renderRow={(row) => {
            const key = `${row.propertyId}|${row.packageId}|${row.year}`;
            const unlockActive = isUnlockActive(row.unlock_until);
            const lockTone = row.locked
              ? (unlockActive ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200')
              : 'bg-emerald-50 text-emerald-700 border-emerald-200';
            const lockLabel = row.locked
              ? (unlockActive ? 'Locked (override active)' : 'Locked')
              : 'Open';
            return (
              <>
                <td className="p-3 min-w-[140px]">
                  <div className="font-medium text-slate-800">{farmLabelById[row.propertyId] || row.propertyId}</div>
                  <div className="text-[10px] font-mono text-slate-400 truncate max-w-[200px]" title={row.propertyId}>{row.propertyId}</div>
                </td>
                <td className="p-3 text-slate-700 text-xs">
                  <div className="font-medium">{editionPackageLabel(row, packageLabels)}</div>
                  <div className="text-[10px] font-mono text-slate-400">{row.packageId}</div>
                </td>
                <td className="p-3 text-slate-700 text-xs">
                  <div className="font-medium">{row.year}</div>
                  <div className="text-[10px] text-slate-400">v{row.current_version}</div>
                </td>
                <td className="p-3 whitespace-nowrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${lockTone}`}>
                    {lockLabel}
                  </span>
                </td>
                <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                  {row.unlock_until ? (
                    <span className={unlockActive ? 'text-emerald-700' : 'text-slate-400 line-through'}>
                      {formatUnlockUntil(row.unlock_until)}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3 text-xs">
                  {row.last_unlock_action ? (
                    <div title={row.last_unlock_action.reason || ''}>
                      <div className="text-slate-700">{row.last_unlock_action.by_email || '—'}</div>
                      <div className="text-[10px] text-slate-400">
                        {row.last_unlock_action.at ? new Date(row.last_unlock_action.at).toLocaleString() : '—'}
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => openUnlockModal(row)}
                    className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-800 hover:bg-emerald-50 hover:border-emerald-300 mr-1 inline-flex items-center gap-1 transition-colors"
                    title="Set or extend unlock window"
                  >
                    <Unlock size={12} />
                    {row.unlock_until ? 'Edit' : 'Set'}
                  </button>
                  {row.unlock_until ? (
                    <button
                      type="button"
                      onClick={() => handleClearUnlock(row)}
                      disabled={clearingKey === key}
                      className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 inline-flex items-center gap-1 disabled:opacity-40"
                      title="Clear unlock"
                    >
                      {clearingKey === key ? <EcointelLottieMark size={12} /> : <X size={12} />}
                      Clear
                    </button>
                  ) : null}
                </td>
              </>
            );
          }}
        />
      )}

      {unlockModal ? (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4"
          onClick={closeUnlockModal}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Unlock size={18} className="text-emerald-600" />
                Set unlock window
              </h3>
              <button
                type="button"
                onClick={closeUnlockModal}
                disabled={submitting}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              <strong className="text-slate-700">{farmLabelById[unlockModal.propertyId] || unlockModal.propertyId}</strong>
              {' \u2192 '}
              <strong className="text-slate-700">{packageLabels[unlockModal.packageId] || unlockModal.packageId}</strong>
              {' \u2192 '}
              <strong className="text-slate-700">{unlockModal.year}</strong>
            </p>

            {actionError ? (
              <div className="mb-3 flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div>{actionError}</div>
              </div>
            ) : null}

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Unlock until (local time)
                </label>
                <input
                  type="datetime-local"
                  value={unlockUntilInput}
                  onChange={(e) => setUnlockUntilInput(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  disabled={submitting}
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Defaults to 7 days from now. RB pushes after this expires will be rejected with <code className="font-mono">report_locked</code>.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Reason (optional, recorded in audit log)
                </label>
                <textarea
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value.slice(0, 200))}
                  rows={2}
                  maxLength={200}
                  placeholder="e.g. Marcus needs to push a methodology correction"
                  className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  disabled={submitting}
                />
                <div className="text-[10px] text-slate-400 text-right mt-0.5">{unlockReason.length}/200</div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeUnlockModal}
                disabled={submitting}
                className="px-4 py-2 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSetUnlock}
                disabled={submitting}
                className="px-4 py-2 text-xs bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium disabled:opacity-50 inline-flex items-center gap-1"
              >
                {submitting ? <><EcointelLottieMark size={12} /> Saving…</> : <><Unlock size={12} /> Set unlock</>}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ReportsAdmin() {
  const { farms } = useAdminData();
  const { formatError, logError } = useUserFacingError();
  const [copy, setCopy] = useState(() => mergeReportsHubCopy(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const farmLabelById = useMemo(() => {
    const m = {};
    (farms || []).forEach((f) => {
      m[f.id] = f.farmName || f.name || f.id;
    });
    return m;
  }, [farms]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'reports'));
        if (!cancelled) {
          setCopy(mergeReportsHubCopy(snap.exists() ? snap.data() : null));
        }
      } catch (e) {
        logError('Reports config load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCopy = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'reports'), buildReportsConfigForSave(copy));
    } catch (e) {
      logError('Save reports copy failed', e);
      alert(`Save failed: ${formatError(e)}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <EcointelLottie message="Loading reports settings..." lottiePixelWidth={88} lottiePixelHeight={110} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
          <FileText size={18} className="text-slate-500" />
          Reports hub copy
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          All user-visible text on Analytics → Reports. Same copy for every property unless you change it here.
        </p>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Page title</label>
              <input
                type="text"
                value={copy.pageTitle}
                onChange={(e) => setCopy((c) => ({ ...c, pageTitle: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Loading message (while page loads)</label>
              <input
                type="text"
                value={copy.loadingMessage}
                onChange={(e) => setCopy((c) => ({ ...c, loadingMessage: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Subtitle (under page title)</label>
            <input
              type="text"
              value={copy.pageSubtitle}
              onChange={(e) => setCopy((c) => ({ ...c, pageSubtitle: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Intro text (optional block below subtitle)</label>
            <textarea
              value={copy.introText}
              onChange={(e) => setCopy((c) => ({ ...c, introText: e.target.value }))}
              rows={4}
              className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              placeholder="Optional extra message; leave empty to hide this block."
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveCopy}
              disabled={saving}
              className="px-5 py-2.5 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save copy'}
            </button>
            {saving && <span className="text-xs text-slate-400 flex items-center gap-1"><EcointelLottieMark size={12} /> Saving...</span>}
          </div>
        </div>
      </div>

      <EditionUnlockManager farms={farms} farmLabelById={farmLabelById} formatError={formatError} />
    </div>
  );
}
